import { SORTABLE_FIELDS } from '../../utils/user-settings';
import { MAX_DIMS_PER_LEVEL, levelDims, levelOf } from '../tracking/tracking-page.format';
import type { PanelViz } from './types';

/** Metric picklist — the same fields the report can sort by. */
export const METRIC_OPTIONS = SORTABLE_FIELDS;

const LABELS: Record<string, string> = Object.fromEntries(SORTABLE_FIELDS.map((f) => [f.value, f.label]));
export const metricLabel = (m: string): string => LABELS[m] ?? m;

const MONEY = new Set(['revenue', 'cost', 'profit', 'epv', 'cpv', 'rpl', 'cpl']);
const PERCENT = new Set(['cr', 'ctr', 'cv_rate']);
const RATIO = new Set(['roas', 'roi']);

/** Rows carry numbers; totals carry pre-rounded strings for ratios. Number() handles both. */
export function formatMetric(metric: string, value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (MONEY.has(metric)) {
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  }
  if (PERCENT.has(metric)) return `${n.toFixed(2)}%`;
  if (RATIO.has(metric)) return n.toFixed(2);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export const VIZ_OPTIONS: { value: PanelViz; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'line', label: 'Line chart' },
  { value: 'bar', label: 'Bar chart' },
  { value: 'pie', label: 'Pie chart' },
  { value: 'stat', label: 'Stat' },
  { value: 'summary', label: 'Summary (attention)' },
];

/**
 * Profitability metrics read as good-when-positive (profit/roi) or good-vs-breakeven
 * (roas ≥ 1), so colour them green/red like the default dashboard. Returns a class suffix.
 */
export function metricSign(metric: string, value: unknown): 'pos' | 'neg' | '' {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (metric === 'profit' || metric === 'roi') return n > 0 ? 'pos' : n < 0 ? 'neg' : '';
  if (metric === 'roas') return n >= 1 ? 'pos' : n > 0 ? 'neg' : '';
  return '';
}

// Calmer palette led by the default dashboard's blue, then green/amber/red/…
export const CHART_COLORS = [
  '#2a78d6', '#0ca30c', '#e0a400', '#d03b3b', '#06b6d4',
  '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b',
];

/** Axis/grid/tooltip tokens, matched to the default dashboard, per theme mode. */
export function chartTokens(mode: 'light' | 'dark') {
  return {
    grid: mode === 'dark' ? '#333B45' : '#e1e0d9',
    axis: mode === 'dark' ? '#8B93A0' : '#898781',
    tooltipBg: mode === 'dark' ? '#1b222c' : '#ffffff',
  };
}

const MONEY_METRICS = new Set(['revenue', 'cost', 'profit', 'epv', 'cpv', 'rpl', 'cpl']);

function shortNum(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(Math.round(n));
}
export const moneyShort = (v: number): string => `$${shortNum(Number(v) || 0)}`;

/** A tick formatter for an axis: currency when every metric on it is money, else plain short. */
export function axisTickFormatter(metrics: string[]): (v: number) => string {
  const allMoney = metrics.length > 0 && metrics.every((m) => MONEY_METRICS.has(m));
  return allMoney ? moneyShort : (v: number) => shortNum(Number(v) || 0);
}

/** Dimensions that read as a sequence, so a chart/table wants them in natural order. */
export const TIME_DIMS = new Set(['date', 'hour', 'date_hour']);

/**
 * Entity groupings the stats endpoint returns live-activity for (ENTITY_MODAL_NAMES on the
 * tracking page). Grouping by one of these lets a table panel show the ActivityCell chip +
 * its click-through popover. The ActivityCell `kind` is the group_by minus the `_id` suffix.
 */
export const ENTITY_ACTIVITY_GROUPINGS = new Set([
  'offer_id', 'network_id', 'traffic_source_id', 'flow_id', 'landing_page_id',
]);
export const activityKind = (groupBy: string): string => groupBy.replace(/_id$/, '');

/** Human labels for every filterable/groupable field (mirrors the server allowlist). */
export const FILTER_FIELD_LABELS: Record<string, string> = {
  traffic_source_id: 'Traffic Source', flow_id: 'Flow', offer_id: 'Offer',
  landing_page_id: 'Landing Page', network_id: 'Network', ad_id: 'Ad',
  campaign_id: 'Campaign', rule_group_id: 'Rule Group', country: 'Country',
  external_id_type: 'External ID Type', event_type: 'Event Type', device: 'Device',
  os: 'OS', os_version: 'OS Version', language: 'Language', region: 'Region',
  referrer_domain: 'Referrer Domain', hour: 'Hour', tags: 'Tag',
  v1: 'V1', v2: 'V2', v3: 'V3', v4: 'V4', v5: 'V5',
  v6: 'V6', v7: 'V7', v8: 'V8', v9: 'V9', v10: 'V10',
};

/** The five entity filters that get a name-resolving picker (EntityFilterPicker). */
export const ENTITY_FILTER_FIELDS = ['traffic_source_id', 'flow_id', 'offer_id', 'landing_page_id', 'network_id'];

/** Non-entity filter fields — edited as a field + free-text value in the panel editor. */
export const DIMENSION_FILTER_FIELDS: { value: string; label: string }[] = [
  'country', 'device', 'os', 'os_version', 'language', 'region', 'referrer_domain',
  'event_type', 'external_id_type', 'ad_id', 'campaign_id', 'rule_group_id', 'tags',
  'v1', 'v2', 'v3', 'v4', 'v5', 'v6', 'v7', 'v8', 'v9', 'v10',
].map((f) => ({ value: f, label: FILTER_FIELD_LABELS[f] ?? f }));

/** Fields the tracking report can be filtered by — gates the row → Tracking deep-link. */
export const TRACKING_FILTERABLE = new Set([...ENTITY_FILTER_FIELDS, ...DIMENSION_FILTER_FIELDS.map((f) => f.value), 'hour']);

/**
 * Order rows by their group, in natural order. Hour must sort numerically (the server's
 * group_key is a string, so "10" would sort before "2"); date/date_hour already sort
 * chronologically as strings. Anything else keeps the server's (metric) order.
 *
 * `order` lets a table read newest-first ('desc') while a chart stays left-to-right
 * chronological ('asc', the default). It applies only to time dimensions — other
 * groupings already arrive sorted by the server's metric.
 */
export function orderRowsByGroup<T extends DimRow>(
  rows: T[], groupBy: string, order: 'asc' | 'desc' = 'asc',
): T[] {
  const dim = levelDims(groupBy)[0];
  const key = (r: T) => dimValue(r, dim);
  let out: T[] | null = null;
  if (dim === 'hour') out = [...rows].sort((a, b) => Number(key(a)) - Number(key(b)));
  else if (dim === 'date' || dim === 'date_hour') {
    out = [...rows].sort((a, b) => key(a).localeCompare(key(b)));
  }
  if (!out) return rows;
  return order === 'desc' ? out.reverse() : out;
}

type DimRow = { group_key?: string; name?: string; keys?: Record<string, string>; names?: Record<string, string> };

export const dimValue = (r: DimRow, dim: string): string => String(r.keys?.[dim] ?? r.group_key ?? '');
export const dimName = (r: DimRow, dim: string): string =>
  (r.keys ? r.names?.[dim] || r.keys[dim] : r.name || r.group_key) || '(none)';
export const rowLabel = (r: DimRow, groupBy: string): string =>
  levelDims(groupBy).map((d) => dimName(r, d)).join(' · ');

export const PIVOT_SERIES_CAP = 8;

export function canCombine(viz: PanelViz, dims: string[], dim: string): boolean {
  if (viz === 'table') return dims.length < MAX_DIMS_PER_LEVEL;
  if ((viz !== 'line' && viz !== 'bar') || dims.length !== 1) return false;
  const times = [dims[0], dim].filter((d) => TIME_DIMS.has(d)).length;
  return viz === 'line' ? times === 1 : times < 2;
}

export function fitLevel(viz: PanelViz, level: string): string {
  const dims = levelDims(level);
  const out = dims.slice(0, 1);
  for (const d of dims.slice(1)) if (canCombine(viz, out, d)) out.push(d);
  return levelOf(out);
}

export function pivotDims(groupBy: string): { time: string; split: string } | null {
  const dims = levelDims(groupBy);
  const time = dims.find((d) => TIME_DIMS.has(d));
  return dims.length === 2 && time ? { time, split: dims.find((d) => d !== time)! } : null;
}

export const RATES = new Set(['roas', 'roi', 'cr', 'ctr', 'cv_rate', 'epv', 'cpv', 'rpl', 'cpl']);

export interface PivotSeries { value: string; name: string }

export function pivotRows(rows: (DimRow & Record<string, unknown>)[], groupBy: string, metric: string, top: PivotSeries[]) {
  const { time, split } = pivotDims(groupBy)!;
  const series = top.map((s, i) => ({ ...s, key: `s${i}` }));
  const keyOf = new Map(series.map((s) => [s.value, s.key]));
  // no row = no traffic: a count is 0 then, a rate has no value
  const missing = RATES.has(metric) ? null : 0;
  const points = new Map<string, Record<string, unknown>>();
  for (const r of orderRowsByGroup(rows, time)) {
    const k = keyOf.get(dimValue(r, split));
    if (!k) continue;
    const at = dimValue(r, time);
    const p = points.get(at) ?? Object.fromEntries([['label', at], ...series.map((s) => [s.key, missing])]);
    p[k] = Number(r[metric]) || 0;
    points.set(at, p);
  }
  return { data: [...points.values()], series };
}
